<template>
    <teleport :to="`#portal-${portal}`" v-if="exists">
        <div
            class="modal fade | shadow-md"
            tabindex="-1"
            :class="[classes, { scrollable: scrollable }]"
            :data-backdrop="closable || 'static'"
            :data-keyboard="closable ? 'true' : 'false'"
            :data-modal-name="name"
            data-testid="activix-modal"
            ref="modal"
        >
            <div class="modal-dialog" :class="[`modal-${size}`, { 'w-full m-0': mdLayout }]">
                <div class="modal-content | flex flex-col border-0 shadow-md | md:rounded">
                    <div class="relative">
                        <div
                            class="absolute z-50 mt-0 top-0.5 right-0 mr-2 | xl:mt-3 xl:top-1.5 xl:mr-6"
                            :class="cancelClassName || 'h-full'"
                            v-if="isCancelable"
                        >
                            <button
                                type="button"
                                class="link-grey-light | p-4 | xl:p-0"
                                @click="cancelModal"
                            >
                                <icon
                                    class="text-xl xl:text-lg"
                                    name="regular/remove"
                                    data-intercom-target="modal.close"
                                />
                            </button>
                        </div>
                    </div>
                    <div
                        class="relative"
                        :class="{
                            'modal-header | py-4 px-6 border-grey-200 | md:rounded-t':
                                $slots.header,
                        }"
                    >
                        <slot name="header" />
                    </div>
                    <div>
                        <slot name="widget" v-if="!loading" />
                    </div>
                    <div
                        class="modal-body"
                        :class="[
                            {
                                'px-6': mdLayout && !noPadding,
                                'p-0': noPadding,
                            },
                            bodyColor,
                        ]"
                    >
                        <activix-spinner :size="50" v-if="loading && !loadingOverlay" />
                        <slot name="body" v-else />

                        <transition name="fade" :duration="loadingDuration">
                            <div
                                class="absolute flex h-full items-center justify-center left-0 top-0 w-full bg-white z-10"
                                :class="{ 'opacity-50': loadingOverlayOpacity }"
                                v-if="loading && loadingOverlay"
                            >
                                <activix-spinner :size="50" v-if="loadingSpinner" />
                            </div>
                        </transition>
                    </div>
                    <div
                        class="modal-footer | py-4 px-6 bg-white border-grey-200 text-center | md:rounded-b"
                        :class="{ 'sticky bottom-0 z-20': stickyFooter }"
                        v-if="$slots.footer"
                    >
                        <slot name="footer" />
                    </div>
                </div>
            </div>
        </div>
    </teleport>
</template>

<script>
    import { debounce, isFunction } from 'lodash-es';
    import { useModal } from '@/hooks/useModal.ts';

    export const colors = ['white', 'gray'];

    export default {
        name: 'ActivixModal',

        emits: ['open', 'opened', 'close', 'closed', 'canceled', 'before-open', 'before-close'],

        props: {
            name: {
                type: String,
                default: '',
            },
            opened: {
                type: Boolean,
                default: false,
            },
            size: {
                type: String,
                default: 'md',
            },
            closable: {
                type: Boolean,
                default: true,
            },
            cancelable: {
                type: Boolean,
                default: false,
            },
            portal: {
                type: String,
                default: 'modal-1',
            },
            loading: {
                type: Boolean,
                default: false,
            },
            loadingOverlay: {
                type: Boolean,
                default: true,
            },
            loadingOverlayOpacity: {
                type: Boolean,
                default: false,
            },
            loadingAnimateEnter: {
                type: Boolean,
                default: false,
            },
            loadingAnimateLeave: {
                type: Boolean,
                default: false,
            },
            loadingSpinner: {
                type: Boolean,
                default: true,
            },
            openable: {
                type: Function,
                default: null,
            },
            alwaysOnTop: {
                type: Boolean,
                default: false,
            },
            scrollable: {
                type: Boolean,
                default: false,
            },
            classes: {
                type: String,
                default: '',
            },
            color: {
                default: 'white',
                validator: (value) => colors.includes(value),
            },

            cancelClassName: {
                type: String,
                default: '',
            },
            stickyFooter: {
                type: Boolean,
                default: false,
            },
            noPadding: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                exists: false,
                // hiding is true only in the actual process of closing the modal
                // once the modal is hidden, hiding is set to false even if
                // the modal is still not visible
                hiding: false,
                // showing is true only in the actual process of opening the modal
                // once the modal is shown, showing is set to false even if
                // the modal is still visible
                showing: false,
            };
        },

        setup() {
            const modalManager = useModal();
            return { modalManager };
        },

        computed: {
            bodyColor() {
                switch (this.color) {
                    case 'gray':
                        return 'bg-gray-100';
                    default:
                        return 'bg-white';
                }
            },

            loadingDuration() {
                const duration = {};

                if (!this.loadingAnimateEnter) {
                    duration.enter = 0;
                }

                if (!this.loadingAnimateLeave && this.loadingSpinner) {
                    duration.leave = 0;
                }

                return duration;
            },

            isCancelable() {
                return this.closable || this.cancelable;
            },
        },

        watch: {
            '$route.name'() {
                this.hide();
            },

            opened(newValue, oldValue) {
                if (newValue !== oldValue) {
                    this.toggle(newValue);
                }
            },
            closable(newValue, oldValue) {
                if (!this.$refs.modal || typeof $ === 'undefined') {
                    return;
                }

                const $modal = $(this.$refs.modal);
                // Bootstrap stores its modal instance under the 'bs.modal' jQuery data key
                const bsModal = $modal.data?.('bs.modal');

                if (!bsModal?.options) {
                    return;
                }

                if (oldValue && !newValue) {
                    $modal.off('keyup.dismiss.bs.modal');
                    bsModal.options.backdrop = 'static';
                    $modal.find("[data-dismiss='modal']").hide();
                } else if (!oldValue && newValue) {
                    bsModal.escape?.();
                    bsModal.options.backdrop = true;
                    $modal.find("[data-dismiss='modal']").show();
                }
            },
        },

        methods: {
            toggle(opened, params) {
                if (opened) {
                    if (isFunction(this.openable) && !this.openable()) {
                        return;
                    }

                    this.exists = true;
                }

                this.$nextTick(() => {
                    if (opened) {
                        this.show(params);
                    } else {
                        this.hide(params);
                    }
                });
            },

            init() {
                $(this.$refs.modal)
                    .on('show.bs.modal', () => {
                        if (this.showing) {
                            return;
                        }
                        this.showing = true;
                        const modalIndex = this.alwaysOnTop
                            ? 99999
                            : 50 + 1 * $('.modal:visible').length;
                        $(this.$refs.modal).css('z-index', modalIndex);
                        this.$nextTick(() => {
                            $('.modal-backdrop').css('z-index', modalIndex - 1);
                        });
                        this.$emit('open');
                    })
                    .on('shown.bs.modal', () => {
                        $(document.body).addClass('modal-open');
                        this.showing = false;
                        this.$emit('opened');
                    })
                    .on('hide.bs.modal', () => {
                        if (this.hidding) {
                            return;
                        }
                        this.hidding = true;
                        this.$emit('close');
                    })
                    .on('hidden.bs.modal', () => {
                        this.hidding = false;
                        if ($('.modal:visible').length) {
                            $(document.body).addClass('modal-open');
                            this.$nextTick(() => {
                                $('.modal-backdrop').css(
                                    'z-index',
                                    48 + $('.modal:visible').length
                                );
                            });
                        }
                        this.$nextTick(() => {
                            this.$emit('closed');
                            this.destroyModal();
                        });
                    });
            },

            destroyModal() {
                this.exists = false;
                $(this.$refs.modal)
                    .off('show.bs.modal')
                    .off('shown.bs.modal')
                    .off('hide.bs.modal')
                    .off('hidden.bs.modal')
                    .off('loaded.bs.modal');
            },

            show(params) {
                this.init();

                this.$emit('before-open', params);

                this.$nextTick(() => {
                    $(this.$refs.modal).modal('show');
                });
            },

            hide(params, nextTick = true) {
                // check if modal is open before emitting before-close event
                if (!this.exists) {
                    return;
                }

                this.$emit('before-close', params);

                if (nextTick) {
                    this.$nextTick(() => {
                        $(this.$refs.modal).modal('hide');
                    });
                } else {
                    $(this.$refs.modal).modal('hide');
                }
            },

            cancelModal() {
                if (!this.isCancelable) {
                    return;
                }

                this.$emit('canceled');
                this.hide();
            },

            handleUpdate() {
                if (this.$refs.modal) {
                    $(this.$refs.modal).modal('handleUpdate');
                }
            },

            scrollToBottom() {
                if (!this.scrollable) {
                    return;
                }

                setTimeout(() => {
                    const body = this.$refs.modal.querySelector('.modal-body');

                    body.scrollTop = body.scrollHeight;
                }, 0);
            },

            onToggle(event) {
                const { name, visible, params } = event;
                if (this.name && this.name === name) {
                    // visible is always explicitly true or false now
                    this.toggle(visible, params);
                }
            },
        },

        created() {
            this.handleUpdate = debounce(this.handleUpdate, 100);
            this.modalManager.subscription.on('toggle', this.onToggle);
        },

        beforeUnmount() {
            this.hide(null, false);
            this.destroyModal();
            this.handleUpdate = null;
            this.modalManager.subscription.off('toggle', this.onToggle);
        },
    };
</script>
